"use client";

import { useClerk } from "@clerk/nextjs";
import { LogOut, Settings, User as UserIcon, ChevronDown, Mail } from "lucide-react";
import { useUser } from "@/auth/hooks/clerk-hooks";
import { use_set_lti_mode } from "@/providers/lti-provider";
import { clear_lti_auth_service_worker } from "@/auth/utils/lti/lti-postmessage-client";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar } from "@/components/ui/avatar";

const user_profile_props = {
	appearance: {
		elements: {
			profileSection__enterpriseAccounts: "hidden",
			profileSection__activeDevices: "hidden",
			profileSectionPrimaryButton__emailAddresses: "hidden",
			menuButtonEllipsis: "hidden",
		},
		layout: {
			unsafe_disableDevelopmentModeWarnings: true,
		}
	}
}

export function NavbarUserDropdown({ is_lti_mode, compact = true }: { is_lti_mode: boolean; compact?: boolean }) {
	const { user } = useUser();
	const { openUserProfile, signOut } = useClerk();
	const set_lti_mode = use_set_lti_mode();

	const user_name =
		user?.fullName ||
		[user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
		"Account";
	const user_email =
		(user as any)?.primaryEmailAddress?.emailAddress ||
		(user as any)?.emailAddresses?.[0]?.emailAddress ||
		"";
	const image_url = !is_lti_mode ? ((user as any)?.imageUrl as string | undefined) : undefined;

	const initials = user_name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((w) => w[0]?.toUpperCase())
		.join("");

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className={`group transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
						compact 
							? "rounded-full p-0.5 hover:bg-slate-100/80" 
							: "flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100/80 w-full"
					}`}
					aria-label="Open user menu"
				>
					<Avatar
						src={image_url}
						alt={user_name}
						fallback={
							is_lti_mode ? (
								<div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-700">
									<UserIcon className="h-4 w-4" />
								</div>
							) : (
								<div className="flex h-full w-full items-center justify-center bg-linear-to-br from-blue-500 to-indigo-500 text-[11px] font-semibold text-white">
									{initials || "U"}
								</div>
							)
						}
						className={`ring-1 ring-slate-200 shadow-sm group-hover:ring-slate-300 ${compact ? "h-8! w-8!" : "h-8! w-8!"}`}
					/>
					{!compact && (
						<>
							<div className="flex flex-col min-w-0 flex-1 text-left">
								<span className="text-sm font-medium text-slate-900 truncate">{user_name}</span>
								{user_email && <span className="text-xs text-slate-500 truncate">{user_email}</span>}
							</div>
							<ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
						</>
					)}
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				align="end"
				alignOffset={compact ? 0 : -30}
				className="w-72 rounded-xl border border-slate-200/70 bg-white/95 p-2 shadow-[0_18px_45px_-20px_rgba(15,23,42,0.45)] backdrop-blur supports-[backdrop-filter]:bg-white/80"
			>
				<DropdownMenuLabel className="rounded-lg border border-slate-200/60 bg-slate-50/70 px-3 py-2.5">
					<div className="flex items-center gap-3">
						<Avatar
							src={image_url}
							alt={user_name}
							fallback={
								is_lti_mode ? (
									<div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-700">
										<UserIcon className="h-5 w-5" />
									</div>
								) : (
									<div className="flex h-full w-full items-center justify-center bg-linear-to-br from-blue-500 to-indigo-500 text-sm font-semibold text-white">
										{initials || "U"}
									</div>
								)
							}
							className="h-10 w-10 ring-1 ring-slate-200 shadow-sm"
						/>
						<div className="flex min-w-0 flex-col">
							<span className="truncate text-sm font-medium text-slate-900">{user_name}</span>
							{user_email ? <span className="truncate text-xs text-slate-500">{user_email}</span> : null}
						</div>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator className="my-2 bg-slate-200/60" />

				{!is_lti_mode ? (
					<DropdownMenuItem
						className="cursor-pointer rounded-lg px-2.5 py-2"
						onSelect={(e) => {
							e.preventDefault();
							openUserProfile(user_profile_props);
						}}
					>
						<div className="flex items-center gap-2">
							<Settings className="h-4 w-4 text-slate-600" />
							<span>Manage account</span>
						</div>
					</DropdownMenuItem>
				) : null}

				<DropdownMenuItem asChild className="cursor-pointer rounded-lg px-2.5 py-2">
					<a
						href="https://internal.support.studystash.com/servicedesk/customer/portals"
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center gap-2"
					>
						<Mail className="h-4 w-4 text-slate-600" />
						<span>Support</span>
					</a>
				</DropdownMenuItem>

				<DropdownMenuSeparator className="my-2 bg-slate-200/60" />

				{is_lti_mode ? (
					<DropdownMenuItem disabled className="rounded-lg px-2.5 py-2 opacity-60">
						<div className="flex items-center gap-2">
							<LogOut className="h-4 w-4 text-slate-600" />
							<span className="text-slate-900">Sign out (managed by your LMS)</span>
						</div>
					</DropdownMenuItem>
				) : (
					<DropdownMenuItem
						className="cursor-pointer rounded-lg px-2.5 py-2"
						onSelect={(e) => {
							e.preventDefault();
							const clear_lti_state = async () => {
								set_lti_mode(false);
								await clear_lti_auth_service_worker();
								try {
									await fetch("/api/lti/signout", {
										method: "POST",
										credentials: "include",
										cache: "no-store",
									});
								} catch {
									// ignore
								}
							};

							void clear_lti_state().finally(() => {
								void signOut({ redirectUrl: "/post-logout" });
							});
						}}
					>
						<div className="flex items-center gap-2">
							<LogOut className="h-4 w-4 text-slate-600" />
							<span className="text-slate-900">Sign out</span>
						</div>
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
